import {
  AddIcon,
  Button,
  CheckboxCellRenderer,
  Select,
  type TypeSelectOption,
  type TypeTableColumn,
} from '@thefarmersfront/kitchen';

import { ClaimExchangeDeliveryItemCell } from '~/components/claim';
import { NumberInputCell } from '~/components/common';

import { formatNumber, getSelectPlacement } from '~/utils/common';

import type {
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

type TypeExchangeOrderItem = TypeExchangeRegisterSecondStepValues['exchangeOrderItems'][number];
type TypeExchangeOrderItemState = TypeExchangeRegisterSecondStepValuesState['exchangeOrderItems'][number];
type TypeExchangeDeliveryItem = TypeExchangeOrderItem['exchangeDeliveryItems'][number];

/**
 * 교환배송 상품 1건당 1행. 주문 상품 칸(입력 포함)은 같은 주문 상품의 행끼리 병합한다.
 * 교환배송 상품이 없는 주문 상품도 한 행으로 보인다. 입력 검증 상태를 행에 함께 실어, 상태가 바뀌어도 셀만 다시 그린다.
 */
export type TypeExchangeGoodsRow = TypeExchangeOrderItem & {
  rowId: string;
  itemIndex: number;
  itemCount: number;
  state: TypeExchangeOrderItemState | undefined;
  deliveryItem: TypeExchangeDeliveryItem | null;
  deliveryItemState: TypeExchangeOrderItemState['exchangeDeliveryItems'][number] | undefined;
};

export const flattenExchangeGoods = (
  items: TypeExchangeOrderItem[],
  states: TypeExchangeOrderItemState[]
): TypeExchangeGoodsRow[] =>
  items.flatMap((item, itemIndex) => {
    const state = states.find((itemState) => itemState.itemId === item.itemId);
    const deliveryItems: (TypeExchangeDeliveryItem | null)[] = item.exchangeDeliveryItems.length
      ? item.exchangeDeliveryItems
      : [null];

    return deliveryItems.map((deliveryItem, index) => ({
      ...item,
      // 행 id 는 순번으로 둬, 교환배송 상품을 더하거나 빼도 주문 상품 칸(첫 행)이 다시 마운트되지 않게 한다.
      rowId: `${item.itemId}-${index}`,
      itemIndex,
      itemCount: items.length,
      state,
      deliveryItem,
      deliveryItemState: state?.exchangeDeliveryItems.find((itemState) => itemState.itemId === deliveryItem?.itemId),
    }));
  });

/** 행 입력 핸들러. 컬럼을 고정하려고 최신 값은 핸들러가 직접 읽는다(렌더마다 바뀌는 값을 컬럼에 넣지 않는다). */
export type TypeExchangeGoodsHandlers = {
  onToggleExchange: (itemId: string, checked: boolean) => void;
  onChangeQuantity: (itemId: string, value: string) => void;
  onBlurQuantity: (itemId: string, value: string) => void;
  onFocusQuantity: (itemId: string) => void;
  onChangeReason: (itemId: string, value: TypeSelectOption<string>) => void;
  onToggleSameGoods: (itemId: string, checked: boolean) => void;
  onOpenExchangeItemAddModal: (itemId: string) => void;
  onDeleteDeliveryItem: (itemId: string, deliveryItemId: string) => void;
  onChangeDeliveryQuantity: (itemId: string, deliveryItemId: string, value: string) => void;
  onBlurDeliveryQuantity: (itemId: string, deliveryItemId: string, value: string) => void;
  onFocusDeliveryQuantity: (itemId: string, deliveryItemId: string) => void;
};

/** 교환사유 드롭다운을 위로 펼치기 시작하는 주문 상품 위치(아래쪽 행은 표 밖으로 잘리지 않게 위로 연다). */
const SELECT_PLACEMENT_THRESHOLD_ROW = 5;

/** 주문 상품 칸. 주문 상품코드로 묶인 행끼리만 병합한다. */
const MERGED = { rowSpan: true } as const;

/**
 * [교환 신청/조회] 교환 신청 등록 모달 > 교환 정보 입력 > 교환 상품 테이블 컬럼 정의
 */
const getClaimExchangeRegisterExchangeItemsColumnDefs = ({
  selectOptions,
  handlers,
}: {
  selectOptions: TypeSelectOption<string>[];
  handlers: TypeExchangeGoodsHandlers;
}) => {
  const columnDefs: TypeTableColumn<TypeExchangeGoodsRow>[] = [
    {
      accessorKey: 'itemId',
      header: '상품코드',
      size: 130,
      meta: { rowSpan: true, rowSpanOrder: 1 },
    },
    {
      accessorKey: 'itemName',
      header: '상품명',
      minSize: 340,
      meta: { flex: true, ...MERGED },
    },
    {
      accessorKey: 'isExchange',
      header: '교환여부',
      size: 76,
      minSize: 76,
      cell: ({ row }) => (
        <CheckboxCellRenderer
          label="교환"
          checked={row.original.isExchange}
          onChange={(e) => handlers.onToggleExchange(row.original.itemId, e.target.checked)}
        />
      ),
      meta: MERGED,
    },
    {
      accessorKey: 'quantity',
      header: '구매수량',
      size: 80,
      cell: ({ getValue }) => {
        const value = getValue<number>();
        return value ? formatNumber(value) : '-';
      },
      meta: { cellStyle: { textAlign: 'right' }, ...MERGED },
    },
    {
      accessorKey: 'exchangeQuantity',
      header: '교환수량',
      meta: { rowSpan: true, headerTooltip: { description: '필수 입력입니다.' } },
      size: 100,
      minSize: 100,
      cell: ({ getValue, row }) => {
        const data = row.original;
        return (
          <NumberInputCell
            value={getValue<string>()}
            disabled={!data.isExchange}
            status={data.state?.exchangeQuantity.status}
            validationMessage={data.state?.exchangeQuantity.message}
            onChange={(value) => handlers.onChangeQuantity(data.itemId, value)}
            onBlur={(value) => handlers.onBlurQuantity(data.itemId, value)}
            onFocus={() => handlers.onFocusQuantity(data.itemId)}
          />
        );
      },
    },
    {
      id: 'exchangeReason',
      accessorFn: (row) => row.exchangeReason?.value ?? null,
      header: '교환사유',
      meta: { rowSpan: true, headerTooltip: { description: '필수 선택입니다.' } },
      size: 147,
      minSize: 147,
      cell: ({ row }) => {
        const data = row.original;
        return (
          <Select
            size="tiny"
            placeholder="선택"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            dropdownPlacement={getSelectPlacement(data.itemIndex, data.itemCount, {
              thresholdRow: SELECT_PLACEMENT_THRESHOLD_ROW,
            })}
            disabled={!data.isExchange}
            options={selectOptions}
            status={data.state?.exchangeReason.status}
            validationMessage={data.state?.exchangeReason.message}
            value={data.exchangeReason ?? null}
            onChange={(_: unknown, value: TypeSelectOption<string>) => handlers.onChangeReason(data.itemId, value)}
          />
        );
      },
    },
    {
      accessorKey: 'isSameGoods',
      header: '동일상품 교환여부',
      meta: {
        rowSpan: true,
        headerTooltip: {
          description:
            '동일한 상품을 재배송한다면 체크해주세요.\n만약 다른 상품을 재배송한다면 [추가] 버튼을 눌러\n배송할 상품을 선택해주세요.',
          maxWidth: '258px',
        },
      },
      size: 148,
      minSize: 76,
      cell: ({ row }) => (
        <CheckboxCellRenderer
          label="동일"
          checked={row.original.isSameGoods}
          disabled={!row.original.isExchange}
          onChange={(e) => handlers.onToggleSameGoods(row.original.itemId, e.target.checked)}
        />
      ),
    },
    {
      id: 'addExchangeItem',
      header: '',
      size: 80,
      minSize: 80,
      cell: ({ row }) => (
        <Button
          size="tiny"
          color="tertiary"
          leftIcon={<AddIcon size={16} />}
          disabled={!row.original.isExchange}
          onClick={() => handlers.onOpenExchangeItemAddModal(row.original.itemId)}
        >
          추가
        </Button>
      ),
      meta: MERGED,
    },
    {
      id: 'exchangeDeliveryItemsCode',
      accessorFn: (row) => row.deliveryItem?.itemId,
      header: '교환배송 상품코드',
      size: 130,
    },
    {
      id: 'exchangeDeliveryItemsName',
      accessorFn: (row) => row.deliveryItem?.itemName,
      header: '교환배송 상품명',
      size: 340,
      cell: ({ getValue, row }) => {
        const { itemId, deliveryItem } = row.original;
        return (
          <ClaimExchangeDeliveryItemCell
            value={getValue<string | undefined>()}
            onClickDeleteButton={() => {
              if (!deliveryItem) return;
              handlers.onDeleteDeliveryItem(itemId, deliveryItem.itemId);
            }}
          />
        );
      },
    },
    {
      id: 'exchangeDeliveryItemsQuantity',
      accessorFn: (row) => row.deliveryItem?.quantity ?? '',
      header: '교환배송수량',
      meta: { headerTooltip: { description: '필수 입력입니다.' } },
      size: 117,
      minSize: 117,
      cell: ({ getValue, row }) => {
        const data = row.original;
        const deliveryItemId = data.deliveryItem?.itemId;
        return (
          <NumberInputCell
            value={getValue<string>()}
            disabled={!deliveryItemId}
            status={data.deliveryItemState?.quantity.status}
            validationMessage={data.deliveryItemState?.quantity.message}
            onChange={(value) => {
              if (deliveryItemId) handlers.onChangeDeliveryQuantity(data.itemId, deliveryItemId, value);
            }}
            onBlur={(value) => {
              if (deliveryItemId) handlers.onBlurDeliveryQuantity(data.itemId, deliveryItemId, value);
            }}
            onFocus={() => {
              if (deliveryItemId) handlers.onFocusDeliveryQuantity(data.itemId, deliveryItemId);
            }}
          />
        );
      },
    },
  ];

  return { columnDefs };
};

export default getClaimExchangeRegisterExchangeItemsColumnDefs;
